import { Component, OnDestroy, OnInit, ViewChild, effect } from '@angular/core';
import { User } from '../models/user';
import { UsersService } from '../services/users.service';
import { Pagination } from 'src/app/shared/models';
import { panelSlider } from 'src/app/shared/content-panels/animations';
import { Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UserDetailViewComponent } from '../user-detail-view/user-detail-view.component';
import { UsersListViewComponent } from '../users-list-view/users-list-view.component';
import { PageEvent } from '@angular/material/paginator';
import {
  ActiveFiltersComponent,
  FilterService,
} from 'src/app/shared/filtering';
import { Sort } from '@angular/material/sort';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { CanDeactivateWithUnsavedChanges } from 'src/app/shared/guards/unsaved-changes.guard';

@Component({
  selector: 'app-users-view-container',
  templateUrl: './users-view-container.component.html',
  styleUrl: './users-view-container.component.scss',
  imports: [
    ActiveFiltersComponent,
    UsersListViewComponent,
    UserDetailViewComponent,
  ],
  animations: [panelSlider],
})
export class UsersViewContainerComponent
  implements OnInit, OnDestroy, CanDeactivateWithUnsavedChanges
{
  @ViewChild(UserDetailViewComponent) detailView: UserDetailViewComponent;

  isDetailOpen = false;
  user: User;

  private _subscriptions: Subscription[] = [];
  private _pagination: Pagination = {
    paging: true,
  };

  constructor(
    public userSrv: UsersService,
    private filterSvc: FilterService,
    private route: ActivatedRoute,
    private router: Router,
    private dialogService: DialogService,
  ) {}

  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;

  hasUnsavedChanges(): boolean {
    return this.detailView?.hasUnsavedChanges() ?? false;
  }

  getSaveChangesDialogConfig() {
    return {
      title: 'Unsaved Changes',
      message:
        'You have unsaved changes. Are you sure you want to leave without saving?',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Leave Page',
    };
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((i) => i.unsubscribe());
  }

  ngOnInit(): void {
    this.filterSvc.initialize('users_list_filters');
    this.getUsers();

    let sub = this.filterSvc.activeFilterChanges$.subscribe(() =>
      this.getUsers(),
    );
    this._subscriptions.push(sub);
  }

  onPageSort(event: Sort) {
    this._pagination.sortBy = event.active;
    this._pagination.sortDirection = event.direction;

    this.userSrv.getUsers(this._pagination);
  }

  onPageChange(event: PageEvent) {
    this._pagination.pageNumber = event.pageIndex + 1;
    this._pagination.pageSize = event.pageSize;
    this.userSrv.getUsers(this._pagination);
  }

  onSortChange(event: Sort) {
    this._pagination.sortBy = event.active;
    this._pagination.sortDirection = event.direction;
    this.userSrv.getUsers(this._pagination);
  }

  selectUser(user: User) {
    this.confirmDiscardIfDirty(() => this.doSelectUser(user));
  }

  closeDetail() {
    this.confirmDiscardIfDirty(() => this.doCloseDetail());
  }

  private confirmDiscardIfDirty(proceed: () => void) {
    if (!this.detailView?.hasUnsavedChanges()) {
      proceed();
      return;
    }

    const dialogConfig = this.getSaveChangesDialogConfig();
    const dialogRef = this.dialogService.openSaveChangesDialog(
      dialogConfig.title,
      dialogConfig.message,
      dialogConfig.cancelButtonText,
      dialogConfig.confirmButtonText,
    );

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.detailView.discardChanges();
        proceed();
      }
    });
  }

  private doSelectUser(user: User) {
    let queryParams: Params = { id: this.user?.Id };
    this.user = user;
    if (this.user) {
      this.isDetailOpen = true;
      queryParams = { id: this.user?.Id };

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge', // remove to replace all query params by provided
      });
    } else {
      this.doCloseDetail();
    }
  }

  private doCloseDetail() {
    this.isDetailOpen = false;
    this.user = null;
    let queryParams: Params = { id: null };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge', // remove to replace all query params by provided
    });
  }

  private getUsers() {
    this.userSrv.getUsers(
      this.filterSvc.activeFilters
        ? this.filterSvc.convertToQueryFilter(
            this.filterSvc.activeFilters,
            true,
          )
        : this._pagination,
    );
  }
}
